import React, {
  ReactFragment,
  useContext,
  useState,
  useEffect,
  useMemo,
  useCallback
} from 'react';
import { AppDeviceSize } from '../../enums/app-device-size';
import { AppStore } from '../../store/app.context';
import { DropDownLink } from '../dropdown-link/dropdown-link';
import { InternalLink } from '../internal-link/internal-link';
import { DropDownMenu } from './dropdown-menu/dropdown-menu';
import { NavbarLink } from './navbar-link/navbar-link';
import { PageBackLink } from '../page-back-link/page-back-link';
import styles from './navbar.module.scss';
import LogoWhite from '../../assets/images/logo-white.svg';
import LogoColor from '../../assets/images/logo-color.svg';
import { Button, IButtonProps } from '../button/button';
import { navbarHeight } from '../../const/settings';
import Envelope from '../../assets/images/estimate.inline.svg';
import { useGlobalTranslations } from '../../hooks/use-global-translations';

interface ILinkChildrenProps {
  name: string;
  url: string; // e.g. "/startup"
  isActive?: boolean;
}

export interface ILinkProps {
  name: string;
  url?: string;
  isActive?: boolean;
  extraClass?: string;
  children?: ILinkChildrenProps[];
}

export interface INavbarProps {
  links?: ILinkProps[];
  navbarTransparent: boolean;
  button: IButtonProps;
  showSection: string;
  hideSection: string;
}

let showMenuSection: HTMLElement | null;
let hideMenuSection: HTMLElement | null;

export const Navbar: React.FunctionComponent<INavbarProps> = (
  props
): ReactFragment => {
  const [showNavbar, setShowNavbar] = useState(true);
  const [showMenu, setShowMenu] = useState<boolean>(false);
  const { state } = useContext(AppStore);
  const { showActiveDevice } = state;
  const [windowScrolled, setWindowScrolled] = useState(false);
  const [mouseOver, setMouseOver] = useState<boolean>(false);
  const { close } = useGlobalTranslations();

  const handleScroll = useCallback((): void => {
    if (window.scrollY > 0) {
      setWindowScrolled(true);
    } else {
      setWindowScrolled(false);
    }

    if (showActiveDevice <= AppDeviceSize.MOBILE) {
      return;
    }

    if (props.showSection && props.hideSection) {
      const sectionShowPostion = showMenuSection?.getBoundingClientRect().top;
      const sectionHidePostion = hideMenuSection?.getBoundingClientRect().top;
      if (!sectionShowPostion && !sectionHidePostion) {
        return;
      }
      if (
        sectionShowPostion <= navbarHeight &&
        sectionHidePostion >= navbarHeight
      ) {
        return setShowNavbar(true);
      }
      if (
        sectionShowPostion <= navbarHeight &&
        sectionHidePostion <= navbarHeight
      ) {
        return setShowNavbar(false);
      }
      setShowNavbar(false);
    } else {
      if (props.showSection) {
        const sectionPostion = showMenuSection?.getBoundingClientRect().top;
        sectionPostion && sectionPostion <= navbarHeight
          ? setShowNavbar(true)
          : setShowNavbar(false);
      }

      if (props.hideSection) {
        const sectionPostion = hideMenuSection?.getBoundingClientRect().top;
        sectionPostion && sectionPostion <= navbarHeight
          ? setShowNavbar(false)
          : setShowNavbar(true);
      }
      return;
    }
  }, [props.showSection, props.hideSection, showActiveDevice]);

  useEffect(() => {
    if (props.showSection) {
      showActiveDevice > AppDeviceSize.MOBILE && setShowNavbar(false);
      showMenuSection = document.getElementById(`${props.showSection}`);
    }

    if (props.hideSection) {
      hideMenuSection = document.getElementById(`${props.hideSection}`);
    }

    handleScroll();
    window.addEventListener('scroll', handleScroll);
    return (): void => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll, props.hideSection, props.showSection, showActiveDevice]);

  const getContactLink = useMemo(() => {
    const contactLink = props.links?.filter(
      link => link.codename === 'link_to_estimate_project'
    );

    return contactLink?.length ? contactLink[0].url : '';
  }, [props.links]);

  const button = useMemo(() => {
    return (
      <React.Fragment>
        {props.button ? (
          <div className={styles.buttonWrapper}>
            <Button {...props.button} className={styles.navabrButton} />
          </div>
        ) : (
          <div className={styles.buttonWrapper}>
            <PageBackLink className={styles.closeBtn}>
              <span className={styles.text}>{close}</span>
              <i className={`material-icons md-primary ${styles.closeIcon}`}>
                clear
              </i>
            </PageBackLink>
          </div>
        )}
      </React.Fragment>
    );
  }, [close, props.button]);

  return (
    <React.Fragment>
      <div className={styles.navbar}>
        {showNavbar && showActiveDevice != null && (
          <React.Fragment>
            <nav
              onMouseOver={(): void => setMouseOver(true)}
              onMouseOut={(): void => setMouseOver(false)}
              className={`${styles.wrapper} ${
                props.navbarTransparent && !windowScrolled && !mouseOver
                  ? styles.transparent
                  : null
              }`}
            >
              {
                <InternalLink url={'/'}>
                  {props.navbarTransparent && !windowScrolled && !mouseOver ? (
                    <img className={styles.logo} src={LogoWhite} alt="logo" />
                  ) : (
                    <img className={styles.logo} src={LogoColor} alt="logo" />
                  )}
                </InternalLink>
              }
              {props.links ? (
                <ul className={styles.navigation}>
                  {showActiveDevice > AppDeviceSize.SMALLDESKTOP ? (
                    props.links.map((link, p) => (
                      <li key={p} className={styles.desktopElement}>
                        {link.children ? (
                          <DropDownLink
                            {...link}
                            colorWhite={
                              props.navbarTransparent &&
                              !windowScrolled &&
                              !mouseOver
                            }
                          />
                        ) : (
                          <NavbarLink
                            {...link}
                            colorWhite={
                              props.navbarTransparent &&
                              !windowScrolled &&
                              !mouseOver
                            }
                          />
                        )}
                      </li>
                    ))
                  ) : (
                    <>
                      <li className={styles.mobileElement}>
                        <InternalLink
                          url={getContactLink ? getContactLink : '/'}
                          className={`${styles.hamburgerMenu} ${styles.contactLink}`}
                        >
                          <Envelope className={styles.mailIcon} />
                        </InternalLink>
                      </li>

                      <li className={styles.mobileElement}>
                        <button
                          className={styles.hamburgerMenu}
                          onClick={(): void => setShowMenu(!showMenu)}
                        >
                          <p
                            className={`material-icons`}
                            style={{ fontSize: '30px' }}
                          >
                            {!showMenu ? 'menu' : 'clear'}
                          </p>
                        </button>
                      </li>
                    </>
                  )}
                </ul>
              ) : (
                button
              )}
            </nav>
            {props.links ? (
              showActiveDevice <= AppDeviceSize.SMALLDESKTOP ? (
                <DropDownMenu {...props} showMenu={showMenu} />
              ) : showMenu ? (
                setShowMenu(false)
              ) : null
            ) : null}
          </React.Fragment>
        )}
      </div>
      {!props.navbarTransparent && <figure className={styles.contentFixed} />}
    </React.Fragment>
  );
};
